/**
 * Floor plan content-extent math for the Punto de Venta plan view.
 * Intentional mirror of the POS app's `app/utils/floor/geometry.ts` helper
 * (the repos share no package): keep both in sync when changing the math.
 */

/**
 * Content bounding box of a sector's placed tables, padded by
 * EXTENT_PADDING_CELLS on every side. `colStart`/`rowStart` are half-cell
 * offsets; `cols`/`rows` are always integers (bounding span + 2 x padding).
 */
export interface IFloorContentExtent {
  colStart: number;
  rowStart: number;
  cols: number;
  rows: number;
}

/** Padding (in grid cells) around the content bounding box. */
export const EXTENT_PADDING_CELLS = 0.5;
/** Preferred cell size (px); drives each sector panel's flex-basis. */
export const TARGET_CELL_PX = 64;
/** Caps panel growth so a lone small sector never renders giant tiles. */
export const MAX_CELL_PX = 96;
/** Minimum panel flex-basis (px) so tiny sectors stay tappable. */
export const MIN_PANEL_BASIS_PX = 220;
/** Panel padding + borders (px) around the sector body. */
export const PANEL_CHROME_PX = 24;

/**
 * Bounding box of the placed entries padded by EXTENT_PADDING_CELLS on
 * every side, unclamped (it may extend past the editor grid; that area is
 * purely visual). Returns null when there are no entries.
 */
export function computeContentExtent(
  entries: Array<{ posX: number; posY: number; w: number; h: number }>
): IFloorContentExtent | null {
  if (!entries.length) return null;

  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;

  for (const entry of entries) {
    const w = entry.w > 0 ? entry.w : 1;
    const h = entry.h > 0 ? entry.h : 1;
    minX = Math.min(minX, entry.posX);
    minY = Math.min(minY, entry.posY);
    maxX = Math.max(maxX, entry.posX + w);
    maxY = Math.max(maxY, entry.posY + h);
  }

  const colStart = minX - EXTENT_PADDING_CELLS;
  const rowStart = minY - EXTENT_PADDING_CELLS;

  return {
    colStart,
    rowStart,
    cols: maxX + EXTENT_PADDING_CELLS - colStart,
    rows: maxY + EXTENT_PADDING_CELLS - rowStart,
  };
}
